import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { JsonLd } from "react-schemaorg";
import Page from "../../layout/Page";
import { usePage } from "../../hooks/CmsHooks";
import CockpitLayoutComponents from "../../components/CockpitLayoutComponents";
import { addBreadcrumbs } from "../../utils/CmsUtils";
import ContentSection from "../../components/ContentSection";
import logo from "../../assets/bjelin-logo.png";
import { unwrapAsset } from "../../utils/CockpitUtils";
import CockpitEditLink from "../../components/CockpitEditLink";
import { useNavigationContext } from "../../functional-components/NavigationContext";
import NotFoundPage from "../NotFoundPage";
import { Navigate } from "react-router-dom";
import GenericOverlay from "../../components/modal/GenericOverlay";

const CmsPage = ({ path, displayAsOverlay = false }) => {
  const _page = usePage(path.replace(/(.+)\/$/, "$1"));
  const { registerCurrentPaths, setAvailableOnSites } = useNavigationContext();
  useEffect(() => {
    registerCurrentPaths(_page.statusCode ? undefined : _page.paths);
    setAvailableOnSites(_page.statusCode ? undefined : _page.sites);
  }, [
    registerCurrentPaths,
    setAvailableOnSites,
    _page.statusCode,
    _page.paths,
    _page.sites
  ]);

  const [content, setContent] = useState(null);
  useEffect(() => {
    const page = addBreadcrumbs(_page);
    setContent(
      displayAsOverlay ? (
        <CockpitLayoutComponents components={_page.content} />
      ) : (
        <CockpitLayoutComponents
          withSectionNav={page.section_nav}
          components={page.content}
        />
      )
    );
  }, [displayAsOverlay, _page]);

  if (path === "/404") {
    return <NotFoundPage />;
  }

  if (path !== _page.path && _page.statusCode !== 404) {
    //The path used is not the correct one for the current language
    return <Navigate to={_page.path} replace />;
  }

  const herobox = _page.content.find(
    section => section.component === "herobox"
  );
  const bkgImage = herobox
    ? unwrapAsset(herobox.settings.background_image)
    : false;
  const image = bkgImage
    ? bkgImage.styles
      ? bkgImage.styles.px828
      : bkgImage.path
    : logo;
  const faq_section = _page.content.find(
    section => section.component === "faq_section"
  );

  console.log("page.content", _page.content);

  return displayAsOverlay ? (
    <GenericOverlay id="cms-page-overlay" key="cms-page-overlay">
      {content}
    </GenericOverlay>
  ) : (
    <>
      {faq_section && (
        <JsonLd
          item={{
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faq_section.children.map(item => {
              return {
                "@type": "Question",
                name: item.settings.question,
                acceptedAnswer: {
                  "@type": "Answer",
                  text: item.settings.answer
                }
              };
            })
          }}
        />
      )}
      <Helmet>
        <meta property="og:site_name" content={"Bjelin"} />
        <meta property="og:type" content={"website"} />
        <meta property="og:title" content={_page.title} />
        {_page.description && (
          <meta
            property="og:description"
            name="description"
            content={_page.description}
          />
        )}
        <meta property="og:image" content={image} />
        {_page.statusCode && (
          <meta name="prerender-status-code" content={_page.statusCode} />
        )}
      </Helmet>
      <Page
        name="cms-page"
        key={"cms-page-" + _page._id}
        title={_page.title}
        noindex={!!_page.noindex}
        nonSrollable={false}
      >
        {content}
        <CockpitEditLink
          collection={"pages"}
          _id={_page._id}
          labelKey={"edit.page"}
          wrapper={children => (
            <ContentSection containerTheme={"white"} className={"text-center"}>
              {children}
            </ContentSection>
          )}
        />
      </Page>
    </>
  );
};

export default CmsPage;
